/**
 * Matemática das calculadoras embutidas no post (E1.3).
 *
 * Funções puras, sem DOM — usadas tanto pelo enriquecimento progressivo do
 * cliente (`src/lib/blogCalculators.ts`) quanto, no futuro, pelo gerador de
 * conteúdo (E5.2), que pode querer pré-calcular um exemplo para a tabela
 * estática do bloco (ver cabeçalho de `registry.ts`).
 *
 * Dinheiro e centavos: toda soma/subtração de valores monetários acontece em
 * CENTAVOS (inteiro), via `toCents`/`fromCents` — evita o erro clássico de
 * ponto flutuante em soma de reais (`0.1 + 0.2 !== 0.3`). A ÚNICA exceção
 * inevitável é a taxa de juros em si: ela é a raiz de uma equação sem forma
 * fechada, então fica em ponto flutuante (`number`) com tolerância explícita
 * (`NEWTON_TOL`, `BISSECAO_TOL`) documentada ao lado do método. Percentuais
 * (PDD, ROI) trafegam como fração 0–1 (0.05 = 5%), não como inteiro — não há
 * centavos de percentual.
 *
 * Erro tipado, nunca `NaN`: toda função devolve `{ ok: true, data }` ou
 * `{ ok: false, codigo, mensagem }`. Um `NaN` vazando para a tela do lojista
 * é pior que uma mensagem de erro — ele parece um resultado válido.
 */

// ---------------------------------------------------------------------------
// Tipos e helpers comuns
// ---------------------------------------------------------------------------

export interface CalcError {
  ok: false;
  /** Código estável, para o cliente decidir a mensagem/telemetria sem parsear texto. */
  codigo:
    | "entrada_invalida"
    | "nao_converge"
    | "sem_solucao_positiva";
  /** Mensagem pronta para exibir ao usuário (pt-BR). */
  mensagem: string;
}

export type CalcResult<T> = { ok: true; data: T } | CalcError;

const erro = (codigo: CalcError["codigo"], mensagem: string): CalcError => ({
  ok: false,
  codigo,
  mensagem,
});

/** Reais (com centavos, ex. 1234.56) → centavos inteiros. */
export const toCents = (valorEmReais: number): number => Math.round(valorEmReais * 100);

/** Centavos inteiros → reais (com centavos). */
export const fromCents = (centavos: number): number => centavos / 100;

const isFiniteNumber = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

// ---------------------------------------------------------------------------
// 1. juros-parcelamento
// ---------------------------------------------------------------------------

export interface JurosParcelamentoInput {
  /** Valor à vista (preço "sem juros"), em reais. */
  valorAVista: number;
  /** Número de parcelas (inteiro ≥ 1). */
  numeroParcelas: number;
  /** Valor de cada parcela, em reais. */
  valorParcela: number;
}

export interface JurosParcelamentoResultado {
  /** Taxa de juros mensal efetiva, como fração (0.0346 = 3,46% a.m.). */
  taxaMensal: number;
  /** A mesma taxa, em percentual, já arredondada para exibição (3.46). */
  taxaMensalPercentual: number;
  /**
   * CET (Custo Efetivo Total) ANUAL aproximado, capitalizando a taxa mensal
   * por 12 meses: `(1 + taxaMensal)^12 - 1`. É uma aproximação porque só
   * enxerga o juro embutido no parcelamento — não há aqui IOF, tarifas ou
   * seguro, que um CET oficial de contrato incluiria. Documentado no rótulo
   * pensando em quem for consumir este campo na UI.
   */
  cetAproximadoAnual: number;
  /** Total pago ao final (soma das parcelas), em reais. */
  totalPago: number;
  /** Juros total pago (totalPago − valorAVista), em reais. Pode ser 0. */
  jurosTotal: number;
  /** Quantas iterações da bisseção foram necessárias, só para depuração/teste. */
  iteracoes: number;
}

/** Fator de valor presente de uma série uniforme (Tabela Price): `(1-(1+i)^-n)/i`. */
const fatorPresenteAnuidade = (i: number, n: number): number => {
  if (i === 0) return n; // limite quando i → 0: o fator vira simplesmente n.
  return (1 - Math.pow(1 + i, -n)) / i;
};

const BISSECAO_TOL = 1e-9; // tolerância na taxa mensal (fração), bem abaixo do 4º dígito percentual exibido.
const BISSECAO_MAX_ITER = 200;
const BISSECAO_MAX_EXPANSOES = 60; // dobra o limite superior até 60 vezes ao buscar o bracket.

/**
 * Resolve a taxa de juros mensal efetiva `i` em
 * `valorAVista = valorParcela × (1 - (1+i)^-n) / i`.
 *
 * MÉTODO ESCOLHIDO: bisseção, não Newton-Raphson.
 *
 * `f(i) = PMT × fatorPresenteAnuidade(i, n) − PV` é estritamente decrescente
 * em i para i ≥ 0 (parcelas fixas valem menos valor presente quanto maior o
 * juro), então o intervalo [0, hi] sempre pode ser "colchetado" (bracketed)
 * expandindo `hi` até `f(hi) ≤ 0`. Bisseção converge de forma garantida nesse
 * cenário, sem depender de chute inicial nem de derivada — Newton-Raphson
 * seria mais rápido, mas pode divergir ou oscilar quando o chute inicial é
 * ruim ou quando `f'(i)` fica perto de zero (juro muito baixo), o que é
 * exatamente a faixa mais comum no crediário. Preferimos previsibilidade a
 * velocidade: a função roda uma vez por clique, não em um hot loop.
 */
export const calcularJurosParcelamento = (
  input: JurosParcelamentoInput
): CalcResult<JurosParcelamentoResultado> => {
  const { valorAVista, numeroParcelas, valorParcela } = input;

  if (!isFiniteNumber(valorAVista) || valorAVista <= 0) {
    return erro("entrada_invalida", "Informe um valor à vista maior que zero.");
  }
  if (!Number.isInteger(numeroParcelas) || numeroParcelas < 1) {
    return erro("entrada_invalida", "O número de parcelas deve ser um inteiro de pelo menos 1.");
  }
  if (!isFiniteNumber(valorParcela) || valorParcela <= 0) {
    return erro("entrada_invalida", "Informe um valor de parcela maior que zero.");
  }

  const pvCentavos = toCents(valorAVista);
  const pmtCentavos = toCents(valorParcela);
  const totalPagoCentavos = pmtCentavos * numeroParcelas;

  // Caso degenerado: total pago é exatamente o valor à vista → juros zero.
  // Precisa funcionar sem cair no laço de bisseção (que exigiria f(0) > 0).
  if (totalPagoCentavos === pvCentavos) {
    return {
      ok: true,
      data: {
        taxaMensal: 0,
        taxaMensalPercentual: 0,
        cetAproximadoAnual: 0,
        totalPago: fromCents(totalPagoCentavos),
        jurosTotal: 0,
        iteracoes: 0,
      },
    };
  }

  // Pagar menos no total do que o valor à vista não tem taxa de juros
  // positiva que resolva a equação (a série ficaria com valor presente maior
  // que PV para qualquer i ≥ 0, e menor para i < 0 — fora do domínio aqui).
  if (totalPagoCentavos < pvCentavos) {
    return erro(
      "sem_solucao_positiva",
      "O total das parcelas é menor que o valor à vista — não há juros positivo que explique essa combinação."
    );
  }

  const f = (i: number): number => pmtCentavos * fatorPresenteAnuidade(i, numeroParcelas) - pvCentavos;

  let lo = 0;
  let hi = 1; // começa em 100% a.m., que já é um teto extremo para crediário.
  let expansoes = 0;
  while (f(hi) > 0 && expansoes < BISSECAO_MAX_EXPANSOES) {
    hi *= 2;
    expansoes++;
  }
  if (f(hi) > 0) {
    return erro(
      "nao_converge",
      "Não foi possível encontrar uma taxa de juros para esses valores. Confira os números informados."
    );
  }

  let iteracoes = 0;
  while (iteracoes < BISSECAO_MAX_ITER && hi - lo > BISSECAO_TOL) {
    const mid = (lo + hi) / 2;
    if (f(mid) > 0) {
      lo = mid;
    } else {
      hi = mid;
    }
    iteracoes++;
  }

  const taxaMensal = (lo + hi) / 2;
  const cetAproximadoAnual = Math.pow(1 + taxaMensal, 12) - 1;

  return {
    ok: true,
    data: {
      taxaMensal,
      taxaMensalPercentual: Math.round(taxaMensal * 100 * 100) / 100, // 2 casas percentuais.
      cetAproximadoAnual,
      totalPago: fromCents(totalPagoCentavos),
      jurosTotal: fromCents(totalPagoCentavos - pvCentavos),
      iteracoes,
    },
  };
};

// ---------------------------------------------------------------------------
// 2. pdd — Provisão para Devedores Duvidosos
// ---------------------------------------------------------------------------

/**
 * Faixa de atraso e sua política de provisão.
 *
 * IMPORTANTE: `percentual` é PARAMETRIZÁVEL pelo usuário da calculadora, não
 * uma norma contábil fixa. Não existe percentual único de PDD por faixa de
 * atraso definido em lei/CPC para o varejo de crediário — cada rede define a
 * própria política de provisionamento (histórico de perda, auditoria, etc.).
 * Os rótulos de faixa (0-30, 31-60, 61-90, 91-180, >180 dias) são só uma
 * convenção usual de aging de carteira; o percentual em si sempre vem de
 * fora. NUNCA fixe um valor default de percentual aqui como se fosse regra.
 */
export interface FaixaPdd {
  /** Rótulo livre da faixa, ex. "0-30 dias". */
  rotulo: string;
  /** Saldo em atraso nessa faixa, em reais. */
  saldo: number;
  /** Percentual de provisão da faixa, como fração 0–1 (0.5 = 50%). */
  percentual: number;
}

export interface FaixaPddResultado extends FaixaPdd {
  /** Provisão calculada da faixa (saldo × percentual), em reais. */
  provisao: number;
}

export interface PddResultado {
  faixas: FaixaPddResultado[];
  totalSaldo: number;
  totalProvisao: number;
}

export const calcularPdd = (faixas: FaixaPdd[]): CalcResult<PddResultado> => {
  if (!Array.isArray(faixas) || faixas.length === 0) {
    return erro("entrada_invalida", "Informe ao menos uma faixa de atraso.");
  }

  for (const faixa of faixas) {
    if (!faixa.rotulo || typeof faixa.rotulo !== "string") {
      return erro("entrada_invalida", "Toda faixa precisa de um rótulo.");
    }
    if (!isFiniteNumber(faixa.saldo) || faixa.saldo < 0) {
      return erro("entrada_invalida", `Saldo inválido na faixa "${faixa.rotulo}".`);
    }
    if (!isFiniteNumber(faixa.percentual) || faixa.percentual < 0 || faixa.percentual > 1) {
      return erro(
        "entrada_invalida",
        `Percentual de provisão inválido na faixa "${faixa.rotulo}" — use uma fração entre 0 e 1.`
      );
    }
  }

  let totalSaldoCentavos = 0;
  let totalProvisaoCentavos = 0;

  const resultadoFaixas: FaixaPddResultado[] = faixas.map(faixa => {
    const saldoCentavos = toCents(faixa.saldo);
    // Arredonda a provisão de cada faixa em centavos individualmente (não a
    // soma) — é o que reconcilia com um lançamento contábil por faixa.
    const provisaoCentavos = Math.round(saldoCentavos * faixa.percentual);
    totalSaldoCentavos += saldoCentavos;
    totalProvisaoCentavos += provisaoCentavos;
    return {
      ...faixa,
      provisao: fromCents(provisaoCentavos),
    };
  });

  return {
    ok: true,
    data: {
      faixas: resultadoFaixas,
      totalSaldo: fromCents(totalSaldoCentavos),
      totalProvisao: fromCents(totalProvisaoCentavos),
    },
  };
};

// ---------------------------------------------------------------------------
// 3. roi-regua-cobranca
// ---------------------------------------------------------------------------

export interface RoiReguaCobrancaInput {
  /** Carteira em atraso, em reais. */
  carteiraEmAtraso: number;
  /** Taxa de recuperação mensal ATUAL, como fração 0–1. */
  taxaRecuperacaoAtual: number;
  /** Taxa de recuperação mensal ESPERADA com a régua/ferramenta, fração 0–1. */
  taxaRecuperacaoEsperada: number;
  /** Custo mensal da ferramenta, em reais. */
  custoMensalFerramenta: number;
}

export interface RoiReguaCobrancaResultado {
  /** Ganho bruto mensal só da diferença de recuperação (sem descontar o custo), em reais. */
  ganhoBrutoMensal: number;
  /** Ganho incremental mensal LÍQUIDO (ganho bruto − custo da ferramenta), em reais. */
  ganhoIncrementalMensal: number;
  /**
   * Meses até o ganho bruto acumulado pagar o custo mensal da ferramenta.
   * `null` quando o ganho bruto mensal é zero ou negativo — nesse caso o
   * investimento nunca se paga com os números informados (não é um erro de
   * entrada, é um resultado válido e importante de mostrar).
   */
  paybackMeses: number | null;
  /**
   * ROI acumulado em 12 meses, como fração (1.5 = 150% de retorno sobre o
   * custo). `null` pela mesma razão do payback: custo mensal só é
   * denominador quando existe ganho bruto para comparar.
   */
  roi12Meses: number | null;
}

export const calcularRoiReguaCobranca = (
  input: RoiReguaCobrancaInput
): CalcResult<RoiReguaCobrancaResultado> => {
  const { carteiraEmAtraso, taxaRecuperacaoAtual, taxaRecuperacaoEsperada, custoMensalFerramenta } = input;

  if (!isFiniteNumber(carteiraEmAtraso) || carteiraEmAtraso < 0) {
    return erro("entrada_invalida", "Informe uma carteira em atraso válida (≥ 0).");
  }
  if (!isFiniteNumber(taxaRecuperacaoAtual) || taxaRecuperacaoAtual < 0 || taxaRecuperacaoAtual > 1) {
    return erro("entrada_invalida", "A taxa de recuperação atual deve ser uma fração entre 0 e 1.");
  }
  if (!isFiniteNumber(taxaRecuperacaoEsperada) || taxaRecuperacaoEsperada < 0 || taxaRecuperacaoEsperada > 1) {
    return erro("entrada_invalida", "A taxa de recuperação esperada deve ser uma fração entre 0 e 1.");
  }
  if (!isFiniteNumber(custoMensalFerramenta) || custoMensalFerramenta <= 0) {
    return erro("entrada_invalida", "Informe um custo mensal da ferramenta maior que zero.");
  }

  const carteiraCentavos = toCents(carteiraEmAtraso);
  const custoCentavos = toCents(custoMensalFerramenta);

  const ganhoBrutoCentavos = Math.round(
    carteiraCentavos * (taxaRecuperacaoEsperada - taxaRecuperacaoAtual)
  );
  const ganhoIncrementalCentavos = ganhoBrutoCentavos - custoCentavos;

  const temGanhoPositivo = ganhoBrutoCentavos > 0;

  return {
    ok: true,
    data: {
      ganhoBrutoMensal: fromCents(ganhoBrutoCentavos),
      ganhoIncrementalMensal: fromCents(ganhoIncrementalCentavos),
      paybackMeses: temGanhoPositivo ? custoCentavos / ganhoBrutoCentavos : null,
      roi12Meses: temGanhoPositivo ? ganhoIncrementalCentavos / custoCentavos : null,
    },
  };
};
